import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';

const fallbackServices = [
  {
    id: '3',
    slug: 'professionel-montage',
    title: { da: "Montagearbejde" }
  },
  {
    id: '1',
    slug: 'professionelt-svejsearbejde',
    title: { da: "Professionelt Svejsearbejde" }
  },
  {
    id: '2',
    slug: 'reparation-af-landbrugsmaskiner',
    title: { da: "Reparation af landbrugsmaskiner" }
  }
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [services, setServices] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/services');
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0));
            setServices(sorted);
          } else {
            setServices(fallbackServices);
          }
        } else {
          setServices(fallbackServices);
        }
      } catch (error) {
        console.error('Error fetching services for header:', error);
        setServices(fallbackServices);
      }
    };
    fetchServices();
  }, []);

  const handleNavClick = (sectionId) => {
    setIsOpen(false);
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 80;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
      isScrolled 
        ? 'border-b border-slate-200 shadow-sm py-3' 
        : 'border-b border-slate-100 py-4'
    }`}>
      {/* Slanted Blue Background for the right half (Desktop only - now lg and up) */}
      <div 
        className="absolute inset-y-0 right-0 left-[45%] lg:left-[50%] bg-[#487aad] z-0 hidden lg:block transition-all duration-300" 
        style={{ clipPath: 'polygon(100px 0, 100% 0, 100% 100%, 0 100%)' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Desktop Layout (Stacked: Logo & CTA top, Nav links bottom left-aligned) - Active from lg (1024px) */}
        <div className="hidden lg:flex flex-col gap-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div 
              onClick={handleLogoClick}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <img 
                src="https://assets.auxodev.dk/assets/e2460b72-5386-46d7-9851-4a7b448e2c89/69bff1cf-990c-49a8-9f87-490bfe4d5aa8.png" 
                alt="Balsby Montage ApS Logo" 
                className="h-14 sm:h-16 md:h-18 w-auto object-contain transition-transform group-hover:scale-102"
              />
            </div>

            {/* Desktop CTA */}
            <div className="flex items-center gap-6">
              <a 
                href="tel:+4593872382" 
                className="flex items-center gap-2 text-sm font-bold text-slate-700 lg:text-white hover:text-[#487aad] lg:hover:text-white/80 transition-colors"
              >
                <Phone className="h-4 w-4 text-[#487aad] lg:text-white" />
                93 87 23 82
              </a>
              <button
                onClick={() => handleNavClick('kontakt')}
                className="bg-[#487aad] lg:bg-black hover:bg-[#3b6591] lg:hover:bg-zinc-900 text-white font-bold px-5 py-2.5 text-sm transition-all duration-200 shadow-md shadow-[#487aad]/10 hover:shadow-[#487aad]/20 cursor-pointer"
              >
                Få et tilbud
              </button>
            </div>
          </div>

          {/* Left-aligned Navigation Links under the Logo */}
          <div className="pt-2">
            <nav className="flex items-center gap-8">
              <button 
                onClick={() => {
                  if (location.pathname === '/') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else {
                    navigate('/');
                  }
                }} 
                className="text-sm font-semibold text-slate-600 hover:text-[#487aad] uppercase tracking-wider transition-colors cursor-pointer"
              >
                Hjem
              </button>
              
              {/* Services Dropdown */}
              <div 
                className="relative py-1"
                onMouseEnter={() => setIsDropdownOpen(true)}
                onMouseLeave={() => setIsDropdownOpen(false)}
              >
                <button 
                  className="flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-[#487aad] uppercase tracking-wider transition-colors cursor-default"
                >
                  <span>Ydelser</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 w-64 bg-white border border-slate-200 shadow-xl py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                    {services.map((service) => {
                      const sSlug = service.slug?.da || service.slug || '';
                      return (
                        <Link
                          key={service.id}
                          to={`/ydelse/${sSlug}`}
                          onClick={() => setIsDropdownOpen(false)}
                          className="block w-full text-left px-4 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-[#487aad] transition-colors border-b border-slate-100 last:border-0 cursor-pointer"
                        >
                          {service.title?.da || service.title}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              <button 
                onClick={() => handleNavClick('om-os')} 
                className="text-sm font-semibold text-slate-600 hover:text-[#487aad] uppercase tracking-wider transition-colors cursor-pointer"
              >
                Om os
              </button>
              <button 
                onClick={() => handleNavClick('kontakt')} 
                className="text-sm font-semibold text-slate-600 hover:text-[#487aad] uppercase tracking-wider transition-colors cursor-pointer"
              >
                Kontakt
              </button>
            </nav>
          </div>
        </div>

        {/* Mobile & Tablet Layout (Standard Header with Hamburger - Active below lg) */}
        <div className="flex lg:hidden items-center justify-between">
          {/* Logo */}
          <div 
            onClick={handleLogoClick}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <img 
              src="https://assets.auxodev.dk/assets/e2460b72-5386-46d7-9851-4a7b448e2c89/69bff1cf-990c-49a8-9f87-490bfe4d5aa8.png" 
              alt="Balsby Montage ApS Logo" 
              className="h-12 w-auto object-contain"
            />
          </div>

          {/* Mobile/Tablet menu button */}
          <div>
            <button
              onClick={() => setIsOpen(true)}
              className="text-slate-700 hover:text-slate-900 transition-colors focus:outline-none"
              aria-label="Åbn menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar Menu on Mobile & Tablet */}
      {/* Backdrop Overlay */}
      <div 
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity duration-300 lg:hidden ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsOpen(false)}
      />

      {/* Sidebar Panel */}
      <div 
        className={`fixed top-0 right-0 bottom-0 w-full max-w-[320px] bg-white z-50 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out lg:hidden ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Sidebar Header with Logo and Close Button */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div 
            onClick={() => {
              setIsOpen(false);
              handleLogoClick();
            }}
            className="flex items-center gap-3 cursor-pointer"
          >
            <img 
              src="https://assets.auxodev.dk/assets/e2460b72-5386-46d7-9851-4a7b448e2c89/69bff1cf-990c-49a8-9f87-490bfe4d5aa8.png" 
              alt="Balsby Montage ApS Logo" 
              className="h-10 w-auto object-contain"
            />
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-slate-700 hover:text-slate-900 transition-colors focus:outline-none"
            aria-label="Luk menu"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Sidebar Content (Scrollable) */}
        <div className="flex-1 overflow-y-auto px-5 py-6 space-y-6">
          <nav className="space-y-1">
            <button
              onClick={() => { 
                setIsOpen(false); 
                if (location.pathname === '/') {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  navigate('/');
                }
              }}
              className="block w-full text-left px-3 py-3 text-sm font-bold text-slate-800 hover:bg-slate-50 hover:text-[#487aad] uppercase tracking-wider transition-all"
            >
              Hjem
            </button>
            
            {/* Mobile Services Dropdown */}
            <div>
              <button
                onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
                className="flex items-center justify-between w-full text-left px-3 py-3 text-sm font-bold text-slate-800 hover:bg-slate-50 hover:text-[#487aad] uppercase tracking-wider transition-all"
              >
                <span>Ydelser</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isMobileDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isMobileDropdownOpen && (
                <div className="pl-4 pr-2 py-1 space-y-1 bg-slate-50 border-l-2 border-[#487aad]/30 mt-1 ml-3">
                  {services.map((service) => {
                    const sSlug = service.slug?.da || service.slug || '';
                    return (
                      <Link
                        key={service.id}
                        to={`/ydelse/${sSlug}`}
                        onClick={() => {
                          setIsOpen(false);
                          setIsMobileDropdownOpen(false);
                        }}
                        className="block w-full text-left py-2 px-3 text-xs font-semibold text-slate-600 hover:text-[#487aad] transition-colors cursor-pointer"
                      >
                        {service.title?.da || service.title}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              onClick={() => handleNavClick('om-os')}
              className="block w-full text-left px-3 py-3 text-sm font-bold text-slate-800 hover:bg-slate-50 hover:text-[#487aad] uppercase tracking-wider transition-all"
            >
              Om os
            </button>
            <button
              onClick={() => handleNavClick('kontakt')}
              className="block w-full text-left px-3 py-3 text-sm font-bold text-slate-800 hover:bg-slate-50 hover:text-[#487aad] uppercase tracking-wider transition-all"
            >
              Kontakt
            </button>
          </nav>

          <div className="border-t border-slate-100 pt-6 flex flex-col gap-4">
            <a 
              href="tel:+4593872382" 
              className="flex items-center gap-3 px-3 py-2.5 text-sm font-bold text-slate-800 hover:text-[#487aad] transition-colors"
            >
              <Phone className="h-5 w-5 text-[#487aad]" />
              93 87 23 82
            </a>
            <button
              onClick={() => handleNavClick('kontakt')}
              className="w-full bg-[#487aad] hover:bg-[#3b6591] text-white font-bold py-3.5 text-center text-sm transition-all shadow-md shadow-[#487aad]/10"
            >
              Få et uforpligtende tilbud
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
